import React, { ReactNode } from "react";
import cssModuleClassNames from "classnames/bind";
import { Link } from "@reach/router";
import _, { omit } from "lodash";
import MenuItem from "@material-ui/core/MenuItem";
import MaterialButton from "@material-ui/core/Button";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import styles from "./buttons.module.css";
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import MenuList from '@material-ui/core/MenuList';

const classNames = cssModuleClassNames.bind(styles);

export interface ButtonProps {
  color: "primary" | "secondary" | "tertiary" | "dangerous";
  size?: "S" | "L";
  marginRight?: boolean;
  marginLeft?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  fullWidth?: boolean;
}

export type HtmlButtonProps = React.DetailedHTMLProps<
React.ButtonHTMLAttributes<HTMLButtonElement>,
HTMLButtonElement
>;

const getClassesFromProps =
  (props: { className?: string } & ButtonProps, withBase?: boolean) => classNames(props.className, {
    base: !!withBase,
    small: props.size !== "L",
    large: props.size === "L",
    primary: props.color === "primary",
    secondary: props.color === "secondary",
    tertiary: props.color === "tertiary",
    dangerous: props.color === "dangerous",
    smallMarginLeft: props.size !== "L" && props.marginLeft,
    smallMarginRight: props.size !== "L" && props.marginRight,
    largeMarginLeft: props.size === "L" && props.marginLeft,
    largeMarginRight: props.size === "L" && props.marginRight,
    fullWidth: props.fullWidth,
  });

/** Styled Buttons
 *
 * Example Screenshots:
 * https://www.figma.com/file/YSnmyD6DN3wnEnjbquUaNCX6/Haven-Design-System
 */
export const Button: React.FC<Omit<ButtonProps & HtmlButtonProps, "href" | "ref">> = (props) => {
  const htmlProps = omit(props, [
    "color",
    "size",
    "disabled",
    "marginLeft",
    "marginRight",
    "className",
  ]);
  const btnClasses = getClassesFromProps(props);

  const variant = props.color === "secondary" ? "outlined" : "contained";
  const color = ["dangerous", "tertiary"].includes(props.color) ? "secondary" : props.color as "primary" | "secondary";
  const size = props.size === "L" ? "large" : "medium";

  return (
    <MaterialButton
      variant={variant}
      color={color}
      size={size}
      className={btnClasses}
      disabled={props.disabled}
      {...htmlProps}
    >
      {props.children}
    </MaterialButton>
  );
};

interface LinkProps {
  color: "primary" | "secondary" | "tertiary" | "dangerous";
  size?: "S" | "L";
  to: string;
  children?: [ReactNode] | string | undefined;
  className?: string;
}

/**
 * Exactly like Button but uses an anchor link instead.
 * It also takes a `to` props for the Reach Router url
 */
export const LinkButton: React.FC<LinkProps & ButtonProps & LinkProps> = (props: LinkProps) => {
  const htmlProps = omit(props, [
    "to",
    "color",
    "size",
    "marginLeft",
    "marginRight",
    "children",
    "ref",
    "className",
    "fullWidth",
  ]);
  const btnClasses = getClassesFromProps(props, true);

  return (
    <Link to={props.to} className={btnClasses} {...htmlProps} style={{ textAlign: "center" }}>
      {props.children}
    </Link>
  );
};

/** A button that is square and has a left arrow icon
 * You need to use an onClick handler to get it to go "back"
 * https://www.figma.com/file/YSnmyD6DN3wnEnjbquUaNCX6/Haven-Design-System
 */
export const BackButton: React.FC<ButtonProps & HtmlButtonProps> = (props: ButtonProps) => {
  let btnStyle = {
    padding: 0,
    fontSize: 14,
    width: 32,
    height: 32,
  };
  if (props.size === "L") {
    btnStyle = {
      padding: 0,
      fontSize: 18,
      width: 48,
      height: 48,
    };
  }
  return (
    <Button style={btnStyle} {...props}>
      <ArrowBackIcon fontSize={props.size === "L" ? "default" : "small"} />
    </Button>
  );
};

type ButtonClickEvent = React.MouseEvent<HTMLButtonElement, MouseEvent>;

export interface DropDownProps extends ButtonProps {
  className?: string,
  options: React.ReactNode[];
  currentOption: number;
  onOptionClick: (index: number) => void;
  onButtonClick?: (index: number, event: ButtonClickEvent) => void;
}

export const DropDownButton: React.FC<DropDownProps & ButtonProps & HtmlButtonProps> =
  (props: DropDownProps) => {
    const {
      size,
      disabled,
      options,
      currentOption,
      onButtonClick,
      onOptionClick,
      color,
    } = props;
    const buttonProps = _.omit(props, ["color", "marginLeft", "marginRight", "className", "onOptionClick", "size"]);
    const btnValue = options[currentOption];
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const [open, setOpen] = React.useState(false);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (onButtonClick) onButtonClick(currentOption, e);
    };

    const handleMenuItemClick = (
      _event: React.MouseEvent<HTMLLIElement, MouseEvent>,
      index: number,
    ) => {
      onOptionClick(index);
      setOpen(false);
    };

    const handleToggle = () => {
      setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
      if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
        return;
      }

      setOpen(false);
    };

    // Convert our color to MUI variant and color
    const variant = color === "secondary" ? "outlined" : "contained";
    const muiColor = ["dangerous", "tertiary"].includes(color) ? "secondary" : color as "primary" | "secondary";
    const muiSize = size === "L" ? "large" : "medium";

    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <ButtonGroup variant={variant} color={muiColor} ref={anchorRef} aria-label="split button" disabled={disabled}>
          <MaterialButton
            onClick={handleClick}
            size={muiSize}
            {...buttonProps}
          >
            {btnValue}
          </MaterialButton>
          <MaterialButton
            size={muiSize}
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="menu"
            onClick={handleToggle}
          >
            <ExpandMoreIcon fontSize={size === "L" ? "default" : "small"} />
          </MaterialButton>
        </ButtonGroup>
        <Popper open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu">
                    {options.map((option, i) => {
                      if (i === currentOption) return null;
                      return (
                        <MenuItem
                          key={i}
                          onClick={(event) => handleMenuItemClick(event, i)}
                        >
                          {option}
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    );
  };

export interface SelectorProps extends ButtonProps {
  text: string;
  isSelected: boolean;
}

export const SelectorButton: React.FC<SelectorProps> = (props: SelectorProps) => <Button
  {...omit(props, "text", "isSelected")}
  color={props.isSelected ? "primary" : "secondary"}
  size="S"
>
  {props.text}
</Button>;

export interface CloseButtonProps {
  onClick: (event: React.MouseEvent) => void
  style?: React.CSSProperties
}

export const CloseButton: React.FC<CloseButtonProps> = ({ onClick, style = {} }) => (
  <span onClick={(event) => {
    event.preventDefault();
    onClick(event);
  }}

  className={styles.closeButton} style={style}>
    ✕
  </span>
);
